import styled from '@emotion/styled';
import defaultTheme from 'ds/theme/default';
import { SmallText, TextLabel, H2 } from 'ds/components/typography';
import { BasicButton } from 'ds/components/button/BasicButton';
import { typographyFontSizeCfg } from 'ds/components/typography/types';
import { colorToAlpha } from 'helpers/insight';
import { InsightType } from 'utils/entities';


export type InsightCardTheme = InsightType | 'ugc';

const cardThemeToColor: Record<InsightCardTheme, string> = {
  ...defaultTheme.colors.insights,
  ugc: defaultTheme.colors.brand.main,
};

export const InsightCardWrapperOuter = styled.div`
  position: relative;
`;

export const InsightCardWrapper = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    background: ${theme.colors.neutrals.white};
    border-radius: ${theme.borderRadius.small};
    margin: 0 ${theme.spacing(1.5)};
    box-shadow: ${theme.shadow.level3};
    width: 260px;
    height: 296px;
    cursor: pointer;
    overflow: hidden;
    transform: scale(1);
    transition: all 300ms;
  `}
  ${({ theme }) => theme.media.showAt({ from: 3 }) `
    :hover {
      transform: scale(1.02);
      box-shadow: ${theme.shadow.level5};
    }
  `}
  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    width: 230px;
    height: 284px;
    margin: 0 ${theme.spacing(1)};
  `}
`;

export const InsightCardContentWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const InsightIconWrapper = styled.div<{ cardTheme: InsightCardTheme }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  border-radius: ${({ theme }) => theme.borderRadius.extraLarge};
  width: auto;
  height: auto;
  background: none;

  svg {
    path {
      fill: ${({ cardTheme }) => cardThemeToColor[cardTheme]}
    }
  }
`;

export const InsightIcon = styled.div<{ cardTheme: InsightCardTheme }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  svg {
    width: 24px;
    height: 24px;
    path {
      fill: ${({ cardTheme }) => cardThemeToColor[cardTheme]}
    }
  }
`;
export const InsightCardContentText = styled(H2.withComponent('div'))`
  flex: 1;
  padding: ${({ theme }) => theme.spacing(3)};
`;

export const InsightCardHeaderRoot = styled.div``;

export const HeaderAndLabelWrapper = styled(TextLabel)<{ cardTheme: InsightCardTheme }>`
  ${({ theme }) => `
    flex-basis: 100%;
    padding: ${theme.spacing(2)} ${theme.spacing(3)};
    background: ${theme.colors.neutrals.white};
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      height: 1px;
      width: 100%;
      left: 0;
      background: ${theme.colors.neutrals.grey8};
    }
  `}
  display: flex;
  justify-content: flex-start;
  align-items: center;
  text-transform: uppercase;
  text-align: center;
  position: relative;

  div:first-of-type {
    font-weight: bold;
  }

  div:nth-of-type(2) {
    text-align: left;
    span {
      background: ${({ theme }) => theme.colors.neutrals.white};
      display: inline-block;
      padding: 0 ${({ theme }) => theme.spacing(0.5)};
    }
  }
`;

export const HeaderText = styled(TextLabel)<{cardTheme: InsightCardTheme}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  margin-${({ theme }) => theme.isRTL ? 'right' : 'left'}: ${({ theme }) => theme.spacing(1)};
  color: ${({ cardTheme }) => cardThemeToColor[cardTheme]};

  > div:nth-of-type(2) {
    display: none;
  }
`;

export const InsightCardTextWrapper = styled(SmallText)<{ cardTheme: InsightCardTheme }>`
  display: flex;
  color: ${({ cardTheme }) => cardThemeToColor[cardTheme]};
  align-items: center;

  svg {
    margin-left: ${({ theme }) => theme.spacing(1)};

    path {
      fill: ${({ cardTheme }) => cardThemeToColor[cardTheme]};
    }
  }
`;
export const InsightCardContentButton = styled.div`
  ${({ theme }) => `
    padding: 0 ${theme.spacing(3)} ${theme.spacing(3)};
  `}
`;

export const InsightCardButton = styled(BasicButton)<{ cardTheme: InsightCardTheme }>`
  padding: 0;
  &, &:hover, &:active {
    border: 1px solid ${({ theme }) => theme.colors.neutrals.transparent};
    background: ${({ cardTheme }) => colorToAlpha(cardThemeToColor[cardTheme], 0.07)};
    color: ${({ cardTheme }) => cardThemeToColor[cardTheme]};
    font-weight: 500;
    font-size: ${typographyFontSizeCfg.text}px;
    text-transform: none;
  }
  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    height: 36px;
  `}
`;

export const InsightDropdownWrapper = styled.div`
  display: inline-flex;
  cursor: pointer;
  width: 23px;
  margin-${({ theme }) => theme.isRTL ? 'right' : 'left'}: auto;
  height: 16px;
  position: relative;
  span {
    width: 32px;
    height: 32px;
    padding: ${({ theme }) => theme.spacing(1)};
    position: static;
    display: block;
    margin-top: -${({ theme }) => theme.spacing(1)};
    transition: all .2s ease;
    &:hover {
      background: ${({ theme }) => theme.colors.neutrals.grey8};
    }
  }
`;
